import { inject, Injectable } from '@angular/core';
import { HttpParams }         from '@angular/common/http';
import { Observable }         from 'rxjs';
import { map }                from 'rxjs/operators';

import { environment }                                                                     from '../../../environments/environment';
import { ConfirmRegistrationData, RegistrationData, ResendConfirmationData, SUCCESS_TYPE } from '../../core/auth/auth.service';
import { RestService }                                                                     from '../../core/rest.service';
import { StringUtils }                                                                     from '../../shared/utils/string.utils';
import { UserData }                                                                        from './user-data';
import { LoginResult } from '../../core/auth/data/login-result';
import { IframeService }                                                                   from '../../core/iframe.service';

export enum UserAction {
  CHANGE_MFA_EMAIL = 'changeMfaEmail',
  DISABLE_MFA_EMAIL = 'disableMfaEmail',
  SET_MFA_EMAIL = 'setMfaEmail'
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private restService = inject(RestService);

  registerUser(registrationData: RegistrationData) {
    return this.restService.post<SUCCESS_TYPE>(environment.api.endpoints.registration, registrationData);
  }

  loginSsoUser(code: string, pingAppName: string, type: string): Observable<LoginResult> {
    const runsInIframe = {runsInIframe: IframeService.runsInIframe()};
    return this.restService.post(environment.api.endpoints.ssoLogin, {code: code, pingAppName: pingAppName, grantType: type}, {params: runsInIframe});
  }

  confirmUserRegistration(confirmRegistrationData: ConfirmRegistrationData) {
    return this.restService.post<SUCCESS_TYPE>(environment.api.endpoints.registrationConfirmation, confirmRegistrationData);
  }

  resendUserConfirmation(resendConfirmationData: ResendConfirmationData) {
    return this.restService.put<SUCCESS_TYPE>(environment.api.endpoints.registrationConfirmation, resendConfirmationData);
  }

  executeUserAction(action: UserAction): Observable<ArrayBuffer> {
    const url = environment.api.endpoints.userAction;

    const httpOptions = {
      params: new HttpParams().set('actionType', action)
    };

    return this.restService.post(url, null, httpOptions);
  }

  logUserAction(actionLog: string, value: string): Observable<ArrayBuffer> {
    const url = environment.api.endpoints.logUserAction;

    const httpOptions = {
      params: new HttpParams().appendAll({'actionLog': actionLog, 'value': value})
    };

    return this.restService.post(url, null, httpOptions);
  }

  revokeJWT(): Observable<ArrayBuffer> {
    const url = environment.api.endpoints.userSession;

    const runsInIframe = {runsInIframe: IframeService.runsInIframe()};
    return this.restService.delete(url, {}, {params: runsInIframe});
  }

  revokeJWTBySignature(token: string): Observable<string> {
    const url = environment.api.endpoints.userSessionSignature;

    return this.restService.delete(url, token);
  }

  checkAgentInvitation(invitationId: string, email: string): Observable<void> {
    return this.restService.get(
      StringUtils.format(environment.api.endpoints.verifyInvitation, invitationId, email));
  }

  getUserAssociatedCustomersData(): Observable<number[]> {
    const apiEndpoint = environment.api.endpoints.userCustomerMappings;
    return this.restService.get(apiEndpoint);
  }

  getUserData(): Observable<UserData> {
    const apiEndpoint = environment.api.endpoints.userData;
    return this.restService.get(apiEndpoint);
  }

  getUserMfaSettings(): Observable<string[]> {
    const apiEndpoint = environment.api.endpoints.userMfaSetting;
    return this.restService.get(apiEndpoint);
  }

  updateUserData(userData: UserData): Observable<void>;
  updateUserData(userData: Partial<UserData>): Observable<void>;
  updateUserData(userData: UserData | Partial<UserData>): Observable<void> {
    const url = environment.api.endpoints.userData;
    return this.restService.patch(url, userData).pipe(map(() => void 0));
  }

  getSuggestedUsername(): Observable<string> {
    return this.restService
      .get<{ suggestedUsername: string }>(environment.api.endpoints.usernameRemediationSuggested)
      .pipe(map(response => response.suggestedUsername));
  }

  validateRemediationUsername(newUsername: string): Observable<void> {
    return this.restService
      .post<void>(environment.api.endpoints.usernameRemediationValidate, {newUsername})
      .pipe(map(() => void 0));
  }

  remediateUsername(newUsername: string): Observable<void> {
    return this.restService
      .post<void>(environment.api.endpoints.usernameRemediation, {newUsername})
      .pipe(map(() => void 0));
  }

}
